import { useState, memo, useMemo } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Search, Dropdown, Loading } from "@components";
import { ArrowPathIcon, BarsArrowDownIcon, ExclamationTriangleIcon, LinkIcon } from "@heroicons/react/24/outline";
import { CheckAndApproveButton } from "features/editor/components/toolbar/CheckAndApproveButton";
import { EDITOR_PAGE_SIZE_KEY } from "features/editor";
import { IEditorFetchParams, ItemsSorting } from "features/workspaces/types";
import { searchBarStyle, searchBarInnerStyle, counterStyle } from "./styles";

const sortingOptions = [
  {
    value: "noSorting",
    enumValue: undefined,
    label: "None",
    icon: "",
  },
  {
    value: "showAlphabeticallyByOriginal",
    enumValue: ItemsSorting.showAlphabeticallyByOriginal,
    label: "Sort alphabetically by original",
    icon: <SortByAlphaIcon width={18} height={18} />,
  },
  {
    value: "showAlphabeticallyByTranslation",
    enumValue: ItemsSorting.showAlphabeticallyByTranslation,
    label: "Sort alphabetically by translation",
    icon: <SortByAlphaIcon width={18} height={18} />,
  },
  {
    value: "showGroupIdSorted",
    enumValue: ItemsSorting.showGroupIdSorted,
    label: "Sort by Group and Id",
    icon: <LinkIcon width={18} height={18} />,
  },
  {
    value: "showSorted",
    enumValue: ItemsSorting.showSorted,
    label: "Sort by status: Invalidated, Unapproved, Approved",
    icon: <BarsArrowDownIcon width={18} height={18} />,
  },
];

export const SearchBar = memo(
  ({
    onSearch,
    approvedCount,
    totalCount,
    handleRefresh,
    fetchParams,
    setFetchParams,
    resetListItems,
    isLoading,
  }: any) => {
    const [sorting, setSorting] = useState<string>("");
    const [sortingDescending, setSortingDescending] = useState<boolean>(false);
    const [filters, setFilters] = useState<string[]>([]);

    const onSortingChange = (event: SelectChangeEvent<string>, newValue: string) => {
      const itemsSort = sortingOptions.find(option => option.value === newValue)?.enumValue;
      const newFetchParams = {
        page: 0,
        itemsSort,
      };

      setSorting(newValue);
      resetListItems();
      setFetchParams((prev: IEditorFetchParams) => ({ ...prev, ...newFetchParams }));
    };

    const onSortingOrderChange = () => {
      setSortingDescending(prevOrder => {
        resetListItems();
        setFetchParams((prev: IEditorFetchParams) => ({ ...prev, ...{ descendingOrder: !prevOrder } }));

        return !prevOrder;
      });
    };

    const onFilterChange = (event: React.MouseEvent<HTMLElement>, filter: string) => {
      setFilters(prevFilters => {
        const isActive = prevFilters.includes(filter);
        const newFilters = isActive ? prevFilters.filter(f => f !== filter) : [...prevFilters, filter];

        const newFetchParams = {
          showInvalidOnly: newFilters.includes("showInvalidOnly"),
        };

        setFetchParams((prev: IEditorFetchParams) => ({ ...prev, ...newFetchParams }));
        resetListItems();
        return newFilters;
      });
    };

    const onPageSizeChange = (event: SelectChangeEvent) => {
      const pageSize = Number(event.target.value);
      localStorage.setItem(EDITOR_PAGE_SIZE_KEY, pageSize.toString());

      resetListItems();
      setFetchParams((prev: IEditorFetchParams) => ({ ...prev, page: 0, pageSize }));
    };

    const handleChange = (event: SelectChangeEvent<string>) => {
      onSortingChange(event, event.target.value);
    };

    const sortOrderSx = useMemo(
      () => ({
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRightWidth: "0 !important",
        p: "7px",

        "& .MuiSvgIcon-root": {
          transform: sortingDescending ? "rotateX(180deg) rotateY(0deg)" : "rotate(0deg) rotateY(0deg)",
          transition: "transform 0.2s ease-in-out",
        },

        "&.Mui-disabled": {
          backgroundColor: "#f5f5f5",
        },
      }),
      [sortingDescending],
    );

    const isSortingOrderEnabled = [
      "showAlphabeticallyByOriginal",
      "showAlphabeticallyByTranslation",
      "showGroupIdSorted",
    ].includes(sorting);

    return (
      <Box sx={searchBarStyle}>
        <Box sx={searchBarInnerStyle}>
          {/* Sorting */}
          <Tooltip title="Sorting order">
            <span>
              <ToggleButton
                value="sortingOrder"
                onClick={onSortingOrderChange}
                sx={sortOrderSx}
                disabled={!isSortingOrderEnabled}
              >
                <ArrowDownwardIcon width={18} height={18} />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Sorting type" placement="top">
            <span>
              <Dropdown
                value={sorting}
                options={sortingOptions}
                onChange={handleChange}
                placeholder={sortingOptions[0]}
              />
            </span>
          </Tooltip>

          {/* Sorting order toggle */}

          {/* Toggle buttons for filters */}
          <ToggleButtonGroup value={filters} exclusive onChange={onFilterChange} sx={{ mr: 1 }}>
            <Tooltip title="Display only invalid entries">
              <ToggleButton value="showInvalidOnly">
                <ExclamationTriangleIcon width={18} height={18} />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          {/* Search */}
          <Search searchText="Original" onSearch={(value: string) => onSearch(value, "searchOriginal")} />
          <Search searchText="Translation" onSearch={(value: string) => onSearch(value, "searchTranslation")} />
          <Search searchText="id " onSearch={(value: string) => onSearch(value, "searchIdInFile")} />
          <Search
            searchText="Group"
            fullMatch
            onSearch={(value: string, fullMatchFlag: boolean) =>
              onSearch(value, "searchGroup", "searchGroupFullMatch", fullMatchFlag)
            }
          />

          {/* Check all lines for errors and approve if no errors */}
          <Box sx={{ mr: 2 }}>
            <CheckAndApproveButton fileId={fetchParams.fileId} refreshList={handleRefresh} />
          </Box>
          <Box sx={counterStyle}>
            {isLoading ? (
              <Loading className="b-loading--bar" />
            ) : (
              <>
                <Tooltip title="Approved count">
                  <span>{approvedCount}</span>
                </Tooltip>
                /
                <Tooltip title="Total count">
                  <span>{totalCount}</span>
                </Tooltip>
              </>
            )}
          </Box>
          <Select value={fetchParams.pageSize} onChange={onPageSizeChange} size="small">
            {window.appConfig.editor.pageSizeOptions?.map((pageSize: number) => (
              <MenuItem key={pageSize} value={pageSize}>
                {pageSize}
              </MenuItem>
            ))}
          </Select>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={handleRefresh}>
              <ArrowPathIcon width={18} height={18} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  },
);
